import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Handoff } from '../../interfaces/handoff-interfaces';
import { getHandoffLog } from '../../api/handoff-client';
import { clockDayTime } from '../../function-utils/shift-time';
import { palette, spacing, typography } from '../../style-utils/theme';
import { Tag } from '../pages/dashboard/dashboard-units-components/dashboard-units.styled';
import { custodyStateOf } from '../iq/iq-handoff-card';
import {
  InboundBanner,
  LastHandoverHead,
  LastHandoverTitle,
  LastHandoverMeta,
  LastHandoverLine,
  LastHandoverItems,
  QuietAction,
} from './shifts.styled';

/* ═══════════ THE DESK'S LAST HANDOVER, BEFORE CLOCK IN ═══════════
 *
 * Owner instruction, 2026-09-11: "before clock in we must neatly display the
 * handover for the specific building always".
 *
 * It REVERSES the 2026-09-04 shape ("the gate is the clock and nothing else"),
 * and it keeps what that decision was protecting. The 2026-08-21 version put
 * the whole handover document above Clock in and pushed the button to y=1128
 * in a 780px viewport. This is a SUMMARY, bounded by construction: who handed
 * over and when, where it stands, the AI headline or at most three flags —
 * each clamped — and one press to read the whole record. The record itself
 * still opens as the handover dialog, full list included.
 *
 * TWO SIZES. The default sits in the page above Clock in (tablet, desktop).
 * `compact` rides in the phone's pinned dock beside Clock in (gate-dock.tsx):
 * one line of who and when, one line of what needs attention, and the same
 * press — the owner's "make sure both clock in and the handover is visible".
 *
 * "FOR THE SPECIFIC BUILDING": fed from `latestFor(buildingId)`, which returns
 * nothing rather than another desk's handover while a building change is in
 * flight. "ALWAYS": taken over, unread or waiting, the newest record at this
 * desk is shown; a desk that has never had one says so in words.
 *
 * READING HERE STAMPS NOTHING. read_at belongs to the recipient dismissing the
 * takeover after clocking in, and a manager or the next concierge glancing at
 * the gate must not move it.
 */

// Enough to know what is waiting; the rest is one press away.
const MAX_FLAGS = 3;

/* ── COMPACT: the pinned dock's version. One line per fact, ellipsised. ── */
const CompactWrap = styled.section`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const CompactTitle = styled.h3`
  margin: 0;
  min-width: 0;
  flex: 1 1 auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
`;

const OneLine = styled.p<{ $meta?: boolean }>`
  margin: 0;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: ${typography.ui};
  font-size: ${({ $meta }) => ($meta ? typography.size.xxs : typography.size.xs)}px;
  line-height: ${typography.leading.normal};
  color: ${({ $meta }) => ($meta ? palette.slate : palette.inkSoft)};

  b {
    font-weight: ${typography.weight.semibold};
    color: ${palette.ink};
    margin-right: ${spacing.half};
  }
`;

const CompactRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  min-width: 0;

  > p {
    flex: 1 1 auto;
  }

  > button {
    flex: none;
  }
`;

export interface HandoffGatePanelProps {
  handoff: Handoff | null;
  /** The inbox is still reading — say so rather than claim there is none. */
  loading: boolean;
  buildingName?: string;
  onRead: () => void;
  /** The phone dock's one-line-per-fact version. */
  compact?: boolean;
}

export const HandoffGatePanel: React.FC<HandoffGatePanelProps> = ({
  handoff,
  loading,
  buildingName,
  onRead,
  compact = false,
}) => {
  // How many entries the shift logged — a courtesy line from the same live
  // list the dialog shows. Asked only of a reader the log route will answer.
  const [count, setCount] = useState<number | null>(null);

  useEffect(() => {
    setCount(null);
    if (!handoff || !handoff.names_visible) return undefined;
    const ac = new AbortController();
    getHandoffLog(handoff.id, { limit: 1 }, { signal: ac.signal })
      .then(res => {
        if (!ac.signal.aborted) setCount(res.total);
      })
      // The panel stands without the number; a failed count is not news.
      .catch(() => undefined);

    return () => ac.abort();
  }, [handoff]);

  const title = buildingName
    ? `Last handover at ${buildingName}`
    : 'Last handover at this desk';
  const emptyText = loading
    ? 'Checking for a handover…'
    : 'No handover has been left at this desk yet.';

  if (!handoff) {
    return compact ? (
      <CompactWrap aria-label={title} aria-busy={loading}>
        <CompactTitle>{title}</CompactTitle>
        <OneLine $meta>{emptyText}</OneLine>
      </CompactWrap>
    ) : (
      <InboundBanner as="section" aria-label={title} aria-busy={loading}>
        <LastHandoverTitle>{title}</LastHandoverTitle>
        <LastHandoverMeta>{emptyText}</LastHandoverMeta>
      </InboundBanner>
    );
  }

  const state = custodyStateOf(handoff);
  const isAi = handoff.status === 'ok' || handoff.status === 'partial';
  const headline = (handoff.summary?.headline || '').trim();
  const flags = (handoff.summary?.sections || []).filter(
    s => s && (s.status === 'incident' || s.status === 'follow_up')
  );
  const shown = flags.slice(0, isAi ? 2 : MAX_FLAGS);
  const hidden = flags.length - shown.length;
  const who = handoff.outgoing_user_name || 'Someone who no longer has an account';
  const entries =
    count != null ? `${count} ${count === 1 ? 'entry' : 'entries'} logged` : '';
  const flagLabel = (s: { label?: string; status?: string }) =>
    s.label || (s.status === 'incident' ? 'Incident' : 'Follow up');
  const readButton = (
    <QuietAction type="button" onClick={onRead}>
      Read the full handover
    </QuietAction>
  );

  if (compact) {
    const first = flags[0];
    const meta = [
      who,
      clockDayTime(handoff.created_at, handoff.window.tz),
      flags.length ? `${flags.length} need attention` : '',
      entries,
    ]
      .filter(Boolean)
      .join(' · ');

    return (
      <CompactWrap aria-label={title}>
        <LastHandoverHead>
          <CompactTitle>{title}</CompactTitle>
          <Tag tone={state.tone}>{state.text}</Tag>
        </LastHandoverHead>
        <OneLine $meta>{meta}</OneLine>
        <CompactRow>
          {/* Ellipsised on screen; the whole sentence is still the element's
              text, so it is read out and found by search. */}
          <OneLine>
            {isAi && headline ? (
              headline
            ) : first ? (
              <>
                <b>{flagLabel(first)}</b>
                {first.text}
              </>
            ) : (
              'Nothing needed attention on this shift.'
            )}
          </OneLine>
          {readButton}
        </CompactRow>
      </CompactWrap>
    );
  }

  const meta = [
    who,
    handoff.outgoing_user_role_label,
    `handed over ${clockDayTime(handoff.created_at, handoff.window.tz)}`,
    entries,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <InboundBanner as="section" aria-label={title}>
      <LastHandoverHead>
        <LastHandoverTitle>{title}</LastHandoverTitle>
        <Tag tone={state.tone}>{state.text}</Tag>
      </LastHandoverHead>
      <LastHandoverMeta>{meta}</LastHandoverMeta>

      {isAi && headline ? <LastHandoverLine>{headline}</LastHandoverLine> : null}

      {shown.length ? (
        <LastHandoverItems aria-label="Needs attention">
          {shown.map((s, i) => (
            <li key={`${s.label || s.status}-${i}`}>
              <b>{flagLabel(s)}</b>
              {s.text}
            </li>
          ))}
        </LastHandoverItems>
      ) : !isAi ? (
        <LastHandoverLine>Nothing needed attention on this shift.</LastHandoverLine>
      ) : null}
      {hidden > 0 ? (
        <LastHandoverMeta>{`+ ${hidden} more to read`}</LastHandoverMeta>
      ) : null}

      <div>{readButton}</div>
    </InboundBanner>
  );
};
